/**
 * Default threshold definitions for driver scorecard fields.
 *
 * Shape per field:
 *   direction: 'higher_better' | 'lower_better'
 *   good / warning: legacy 2/3-tier numeric boundaries (exact semantics depend on direction)
 *   boundaries: generalized N-tier boundary array (2-5 bands, i.e. 1-4 boundary values),
 *               ordered from best to worst per `direction`. Takes priority over good/warning
 *               when present. See resolveBandBoundaries()/getFieldClass() below.
 *   For compiled_score: elite / solid / coaching boundaries (4-tier, NOT part of the
 *   generalized N-tier system below — the compiled score keeps its own fixed shape).
 *   For customer_requests: only a 'good' boundary by default (>= good → success, else warning)
 *
 * Rendering classes produced by getFieldClass() / _bandClass():
 *   N bands are colored from a fixed best→worst palette (BAND_PALETTES below) so the
 *   worst band is always 'text-danger' and the second-worst is always 'text-warning'
 *   (for bands >= 3) — this is what the Flagged Drivers severity logic keys off of,
 *   regardless of how many tiers a tenant has configured for a given metric.
 *   compiled_score (4-tier, main):   value >= elite → compiled-score-elite, >= solid → compiled-score-solid,
 *                                    >= coaching → compiled-score-coaching, else compiled-score-highrisk
 *   compiled_score (comparison):     same numeric bands but Bootstrap text classes
 */

// Threshold keys used for critical/warning "flagging" on the Flagged Drivers tab
// and the Coaching Portal (see FLAG_METRICS in utils/scorecard_flags.js, which owns
// the per-metric labels/notes/severity copy). Kept here, not just there, so the
// tenant config modal can block an admin from dropping one of these fields to 2
// tiers — the 2-band palette's worst class caps at 'text-warning' and can never
// reach 'text-danger', which would silently disable that metric's ability to ever
// flag critical. Keep this list in sync with the `thresholdKey` values in
// utils/scorecard_flags.js's FLAG_METRICS.
export const FLAG_METRIC_THRESHOLD_KEYS = [
  'risk_score',
  'dvir_completion_rate',
  'speeding_percentage',
  'distracted_driving_percentage',
  'following_distance_per_10k_miles',
  'harsh_braking_per_10k_miles',
]

export const DEFAULT_SCORECARD_THRESHOLDS = {
  risk_score: {
    label: 'Safety Score',
    direction: 'higher_better',
    good: 80,
    warning: 50,
  },
  risk_rank: {
    label: 'Safety Rank (Percentile)',
    direction: 'lower_better',
    good: 25,
    warning: 75,
  },
  idling_rank: {
    label: 'Idling Rank (Percentile)',
    direction: 'lower_better',
    good: 25,
    warning: 75,
  },
  idling_percentage: {
    label: 'Idling %',
    direction: 'lower_better',
    good: 21.8,
    warning: 30,
  },
  miles_per_gallon: {
    label: 'MPG',
    direction: 'higher_better',
    good: 7.1,
    warning: 6.6,
  },
  dvir_completion_rate: {
    label: 'DVIR Completion %',
    direction: 'higher_better',
    good: 80,
    warning: 50,
  },
  customer_requests: {
    label: 'Customer Requests',
    direction: 'higher_better',
    good: 1,
  },
  customer_requests_per_10k_miles: {
    label: 'Customer Requests / 10k Miles',
    direction: 'higher_better',
    good: 1,
  },
  compiled_score: {
    label: 'Combined Score',
    direction: 'higher_better',
    // FI-2018: lowered from 85/65/40 — real fleet data clusters mostly 65-95 with a
    // median around 78, so 85 only reached the top ~25% of driver-months. Same
    // 20/25-point gaps preserved, just shifted down 5.
    elite: 80,
    solid: 60,
    coaching: 35,
  },
  speeding_percentage: {
    label: 'Speeding %',
    direction: 'lower_better',
    good: 1,
    warning: 2,
  },
  distracted_driving_percentage: {
    label: 'Distracted Driving / 10k',
    direction: 'lower_better',
    good: 1,
    warning: 5,
  },
  following_distance_per_10k_miles: {
    label: 'Following Distance / 10k',
    direction: 'lower_better',
    good: 1,
    warning: 5,
  },
  harsh_braking_per_10k_miles: {
    label: 'Harsh Braking / 10k',
    direction: 'lower_better',
    good: 1,
    warning: 5,
  },
  weekend_availability_percentage: {
    label: 'Weekend Availability %',
    direction: 'higher_better',
    good: 80,
    warning: 50,
  },
}

/**
 * Optional scorecard fields — not colored by default.
 * A field only becomes active (colored) when the tenant explicitly configures it
 * and saves it into scorecard_thresholds, at which point it appears in mergeThresholds().
 */
export const OPTIONAL_SCORECARD_DEFAULTS = {
  idle_hours: {
    label: 'Idle Hours',
    direction: 'lower_better',
    good: 10,
    warning: 25,
  },
  fuel_consumed_gallons: {
    label: 'Fuel Consumed (gal)',
    direction: 'lower_better',
    good: 200,
    warning: 350,
  },
  bus_days: {
    label: 'Bus Days',
    direction: 'higher_better',
    good: 18,
    warning: 10,
  },
}

/**
 * UI configuration for every configurable scorecard field.
 * Used by the threshold modal to drive sliders generically.
 *
 *   tier   only '4' (triple-handle: elite / solid / coaching — compiled score only) is
 *          ever checked directly. Any other value (historically '2' or '3', kept for
 *          existing field configs) renders the generalized N-tier boundary UI, whose
 *          actual handle count comes from the field's `boundaries` array length, not
 *          this value.
 *   scale  slider max value
 *   step   slider / number input increment
 *   minVal minimum value for the slider (defaults to 0 when absent)
 */
export const THRESHOLD_FIELD_CONFIG = {
  risk_score:                       { label: 'Safety Score',                direction: 'higher_better', tier: '3', scale: 100, step: 1   },
  risk_rank:                        { label: 'Safety Rank (Percentile)',    direction: 'lower_better',  tier: '3', scale: 100, step: 1   },
  idling_rank:                      { label: 'Idling Rank (Percentile)',    direction: 'lower_better',  tier: '3', scale: 100, step: 1   },
  idling_percentage:                { label: 'Idling %',                    direction: 'lower_better',  tier: '3', scale: 100, step: 0.5 },
  miles_per_gallon:                 { label: 'MPG',                         direction: 'higher_better', tier: '3', scale: 15,  step: 0.1 },
  dvir_completion_rate:             { label: 'DVIR Completion %',           direction: 'higher_better', tier: '3', scale: 100, step: 1   },
  customer_requests:                { label: 'Customer Requests',           direction: 'higher_better', tier: '2', scale: 20,  step: 1,   minVal: 1 },
  customer_requests_per_10k_miles:  { label: 'Cust. Requests / 10k Mi',    direction: 'higher_better', tier: '2', scale: 10,  step: 0.1 },
  compiled_score:                   { label: 'Combined Score',              direction: 'higher_better', tier: '4', scale: 100, step: 1   },
  speeding_percentage:              { label: 'Speeding %',                  direction: 'lower_better',  tier: '3', scale: 10,  step: 0.1 },
  distracted_driving_percentage:    { label: 'Distracted Driving / 10k',   direction: 'lower_better',  tier: '3', scale: 10,  step: 0.1 },
  following_distance_per_10k_miles: { label: 'Following Distance / 10k',   direction: 'lower_better',  tier: '3', scale: 10,  step: 0.1 },
  harsh_braking_per_10k_miles:      { label: 'Harsh Braking / 10k',        direction: 'lower_better',  tier: '3', scale: 10,  step: 0.1 },
  weekend_availability_percentage:  { label: 'Weekend Availability %',     direction: 'higher_better', tier: '3', scale: 100, step: 1   },
  idle_hours:                       { label: 'Idle Hours',                  direction: 'lower_better',  tier: '3', scale: 100, step: 0.5 },
  fuel_consumed_gallons:            { label: 'Fuel Consumed (gal)',         direction: 'lower_better',  tier: '3', scale: 1000, step: 5  },
  bus_days:                         { label: 'Bus Days',                    direction: 'higher_better', tier: '3', scale: 25,  step: 1   },
}

/** Keys that are always shown in the threshold modal (colored by default). */
export const DEFAULT_THRESHOLD_KEYS = Object.keys(DEFAULT_SCORECARD_THRESHOLDS)

/**
 * Merge tenant threshold overrides onto the defaults.
 * When tenantThresholds is null/undefined (never saved), falls back to DEFAULT_SCORECARD_THRESHOLDS.
 * When tenantThresholds is an object (even {}), only the fields present in it are colored —
 * a tenant can remove any field including defaults by saving without it.
 * Optional fields are only included when the tenant has explicitly added them.
 *
 * @param {Object|null} tenantThresholds  - parsed scorecard_thresholds from the API response
 * @returns {Object} merged thresholds keyed by field name
 */
export function mergeThresholds(tenantThresholds) {
  // null/undefined means "never configured" — use hardcoded defaults
  // An empty object {} means the tenant explicitly cleared all fields — return empty (no coloring)
  if (tenantThresholds === null || tenantThresholds === undefined) {
    return { ...DEFAULT_SCORECARD_THRESHOLDS }
  }
  if (typeof tenantThresholds !== 'object') {
    return { ...DEFAULT_SCORECARD_THRESHOLDS }
  }
  const merged = {}

  // Include only fields the tenant has explicitly configured
  for (const key of Object.keys(DEFAULT_SCORECARD_THRESHOLDS)) {
    const def = DEFAULT_SCORECARD_THRESHOLDS[key]
    const override = tenantThresholds[key]
    if (override && typeof override === 'object') {
      merged[key] = _mergeFieldOverride(def, override, ['good', 'warning', 'elite', 'solid', 'coaching'])
    }
    // no else — if the tenant removed this field, don't color it
  }

  // Optional fields — only included when the tenant has explicitly configured them
  for (const key of Object.keys(OPTIONAL_SCORECARD_DEFAULTS)) {
    if (tenantThresholds[key] && typeof tenantThresholds[key] === 'object') {
      const def = OPTIONAL_SCORECARD_DEFAULTS[key]
      const override = tenantThresholds[key]
      merged[key] = _mergeFieldOverride(def, override, ['good', 'warning'])
    }
  }

  return merged
}

function _mergeFieldOverride(def, override, numericProps) {
  const mf = { ...def }
  for (const prop of numericProps) {
    if (override[prop] !== undefined && override[prop] !== null && !isNaN(Number(override[prop]))) {
      mf[prop] = Number(override[prop])
    }
  }
  // Generalized N-tier boundaries (2-5 bands = 1-4 boundary values), ordered best->worst
  // per direction. Takes priority over good/warning in _bandClass()/resolveBandBoundaries().
  if (Array.isArray(override.boundaries)) {
    const nums = override.boundaries.map(Number).filter(n => isFinite(n))
    if (nums.length >= 1 && nums.length <= 4) {
      mf.boundaries = nums
    } else {
      delete mf.boundaries
    }
  }
  return mf
}

/**
 * Return the Bootstrap/CSS class for a scorecard field, using its generalized N-tier
 * `boundaries` (2-5 bands) when present, else falling back to the legacy good/warning shape.
 *
 * @param {string} fieldName  - key in thresholds (e.g. 'risk_score')
 * @param {number|null} value - the raw metric value
 * @param {Object} thresholds - merged thresholds from mergeThresholds()
 * @param {Object} [context]  - extra data needed for some fields, e.g. { total_drivers }
 * @returns {string} CSS class
 */
export function getFieldClass(fieldName, value, thresholds, context = {}) {
  if (value === null || value === undefined) return ''

  const cfg = thresholds[fieldName]
  if (!cfg) return ''

  const v = Number(value)
  if (!isFinite(v)) return ''

  if (fieldName === 'compiled_score') {
    return getCompiledScoreClass(v, cfg)
  }

  if (fieldName === 'compiled_score_comparison') {
    return getCompiledScoreComparisonClass(v, cfg)
  }

  if (fieldName === 'risk_rank' || fieldName === 'idling_rank') {
    if (!context.total_drivers || context.total_drivers === 0) return ''
    const percentile = (v / context.total_drivers) * 100
    return _bandClass(percentile, cfg, 'lower_better')
  }

  return _bandClass(v, cfg, cfg.direction)
}

// Best->worst color palettes keyed by band count (boundaries.length + 1).
// The worst band is always 'text-danger' and the second-worst is always 'text-warning'
// (for 3+ bands) so severity-based logic (Flagged Drivers) keeps working regardless of
// how many tiers a metric is configured with. 2-band fields intentionally cap at
// 'text-warning' (never critical) — this mirrors the original customer_requests behavior.
const BAND_PALETTES = {
  2: ['text-success', 'text-warning'],
  3: ['text-success', 'text-warning', 'text-danger'],
  4: ['text-success', 'text-info', 'text-warning', 'text-danger'],
  5: ['text-success', 'text-info', 'text-primary', 'text-warning', 'text-danger'],
}

/**
 * Resolve a field's ordered boundary list (best->worst per direction), whether it was
 * configured via the generalized `boundaries` array or the legacy `good`/`warning` pair.
 * Returns an array of 1-4 numbers, or [] if the field has no usable boundaries.
 */
export function resolveBandBoundaries(cfg) {
  if (!cfg) return []
  if (Array.isArray(cfg.boundaries) && cfg.boundaries.length > 0) {
    return cfg.boundaries.map(Number).filter(n => isFinite(n)).slice(0, 4)
  }
  const boundaries = []
  if (cfg.good !== undefined && cfg.good !== null && isFinite(Number(cfg.good))) boundaries.push(Number(cfg.good))
  if (cfg.warning !== undefined && cfg.warning !== null && isFinite(Number(cfg.warning))) boundaries.push(Number(cfg.warning))
  return boundaries
}

function _bandClass(v, cfg, direction) {
  const boundaries = resolveBandBoundaries(cfg)
  if (!boundaries.length) return ''
  const bandCount = Math.min(boundaries.length + 1, 5)
  const palette = BAND_PALETTES[bandCount] || BAND_PALETTES[3]
  for (let i = 0; i < boundaries.length; i++) {
    const b = boundaries[i]
    if (direction === 'higher_better') {
      // v >= boundary[i] → band i (boundaries ordered best->worst, i.e. descending)
      if (v >= b) return palette[i]
    } else {
      // lower_better: v < boundary[0] → band 0 (strict), v <= boundary[i>0] → band i
      // (inclusive). The asymmetry on the first boundary matches the original
      // good/warning behavior and keeps a good==warning edge case in the worse band.
      if (i === 0 ? v < b : v <= b) return palette[i]
    }
  }
  return palette[boundaries.length]
}

// A driver the backend tiered "Insufficient Data" still carries a numeric compiled_score
// that's a renormalized artifact over too little activity to trust, so it must not be
// colored as if it were a real Elite/Solid/etc. score. Both class helpers below short-
// circuit this tier to a neutral (uncolored) result rather than recomputing from cfg.
const INSUFFICIENT_DATA_TIER = 'Insufficient Data'

// Tier-name -> CSS class lookups, used when a row already carries a backend-computed
// compiled_score_tier (always true for rows coming off get_all_drivers_json et al).
const TIER_TO_COMPILED_SCORE_CLASS = {
  Elite: 'compiled-score-elite',
  Solid: 'compiled-score-solid',
  Fair: 'compiled-score-coaching',
  'High Risk': 'compiled-score-highrisk',
}

const TIER_TO_COMPILED_SCORE_COMPARISON_CLASS = {
  Elite: 'text-success',
  Solid: 'text-primary',
  Fair: 'text-warning',
  'High Risk': 'text-danger',
}

/**
 * 4-tier class for the main scorecard badge (uses scoped CSS classes). Prefers the
 * backend-computed `tier` (compiled_score_tier) when given, since that's the only
 * value that correctly reflects Insufficient Data; falls back to recomputing from
 * the raw score against cfg.elite/solid/coaching cutoffs for callers that don't
 * have a tier handy.
 */
export function getCompiledScoreClass(score, cfg, tier) {
  if (tier === INSUFFICIENT_DATA_TIER) return ''
  if (tier && TIER_TO_COMPILED_SCORE_CLASS[tier]) return TIER_TO_COMPILED_SCORE_CLASS[tier]
  if (!cfg) return ''
  if (score === null || score === undefined) return ''
  const s = Number(score)
  if (!isFinite(s)) return ''
  const elite    = cfg.elite    !== undefined ? cfg.elite    : DEFAULT_SCORECARD_THRESHOLDS.compiled_score.elite
  const solid    = cfg.solid    !== undefined ? cfg.solid    : DEFAULT_SCORECARD_THRESHOLDS.compiled_score.solid
  const coaching = cfg.coaching !== undefined ? cfg.coaching : DEFAULT_SCORECARD_THRESHOLDS.compiled_score.coaching
  if (s >= elite)    return 'compiled-score-elite'
  if (s >= solid)    return 'compiled-score-solid'
  if (s >= coaching) return 'compiled-score-coaching'
  return 'compiled-score-highrisk'
}

/**
 * 4-tier class for the comparison table (uses Bootstrap text classes). See
 * getCompiledScoreClass() above for why `tier` takes priority over recomputing from cfg.
 */
export function getCompiledScoreComparisonClass(score, cfg, tier) {
  if (tier === INSUFFICIENT_DATA_TIER) return ''
  if (tier && TIER_TO_COMPILED_SCORE_COMPARISON_CLASS[tier]) return TIER_TO_COMPILED_SCORE_COMPARISON_CLASS[tier]
  if (!cfg) return ''
  if (score === null || score === undefined) return ''
  const s = Number(score)
  if (!isFinite(s)) return ''
  const elite    = cfg.elite    !== undefined ? cfg.elite    : DEFAULT_SCORECARD_THRESHOLDS.compiled_score.elite
  const solid    = cfg.solid    !== undefined ? cfg.solid    : DEFAULT_SCORECARD_THRESHOLDS.compiled_score.solid
  const coaching = cfg.coaching !== undefined ? cfg.coaching : DEFAULT_SCORECARD_THRESHOLDS.compiled_score.coaching
  if (s >= elite)    return 'text-success'
  if (s >= solid)    return 'text-primary'
  if (s >= coaching) return 'text-warning'
  return 'text-danger'
}

// ---- shared tier + band presentation (FI-2047) ----
// one source of truth for tier chips/inks/fills and chart hues; the css family
// (.sc-tier-*, .compiled-score-*, .sc-band-*) lives in
// assets/css/driver-manager-mobile.css so the dashboard, the embedded comparison,
// and the main-dashboard widget all draw from the same rules.

// css key per tier ('coaching' is the historical css key for the Fair tier;
// kept so class names don't churn under midnight overrides)
export const TIER_KEY = {
  Elite: 'elite',
  Solid: 'solid',
  Fair: 'coaching',
  'High Risk': 'highrisk',
  [INSUFFICIENT_DATA_TIER]: 'insufficient',
}

/**
 * Shared class string for a tier in one presentation variant:
 *   'chip' (read-only pill badge), 'text' (ink only), 'fill' (bar fill),
 *   'outline' (outlined filter chip; add tierClass(tier, 'outline--on') via
 *   the caller's active state for the selected saturated fill).
 * Unknown tiers get the neutral insufficient treatment.
 */
export function tierClass(tier, variant = 'chip') {
  const key = TIER_KEY[tier] || 'insufficient'
  if (variant === 'chip') return `sc-tier-chip sc-tier-chip--${key}`
  return `sc-tier-${variant}--${key}`
}

// chart hues per tier. light = the long-standing bootstrap tier hues; the
// midnight set steps each hue onto the validated dark-chart hue families
// (utils/alerts_analytics.js practice: dark mode is selected, never flipped).
const TIER_COLORS_LIGHT = {
  Elite: '#28a745',
  Solid: '#007bff',
  Fair: '#ffc107',
  'High Risk': '#dc3545',
  [INSUFFICIENT_DATA_TIER]: '#adb5bd',
}
const TIER_COLORS_DARK = {
  Elite: '#27a075',
  Solid: '#3987e5',
  Fair: '#bd8524',
  'High Risk': '#e46666',
  [INSUFFICIENT_DATA_TIER]: '#5f6e7d',
}

export function getTierColor(tier, theme) {
  const map = theme === 'midnight' ? TIER_COLORS_DARK : TIER_COLORS_LIGHT
  return map[tier] || map[INSUFFICIENT_DATA_TIER]
}

/**
 * Presentation-agnostic band lookup for a metric value against its (merged)
 * thresholds: { band, count, key } where band 0 is best and `key` is the
 * band's semantic color name (success/info/primary/warning/danger, the same
 * best->worst order BAND_PALETTES colors text with). Returns null when the
 * field has no usable config or the value is missing -- callers render
 * nothing. This is getFieldClass()'s presentation-agnostic twin: the fleet
 * table's band dots consume it without touching the text-danger/-warning
 * strings the Flagged Drivers severity logic keys off.
 */
export function getFieldBand(fieldName, value, thresholds, context = {}) {
  if (value === null || value === undefined) return null
  const cfg = thresholds[fieldName]
  if (!cfg) return null
  let v = Number(value)
  if (!isFinite(v)) return null
  let direction = cfg.direction
  if (fieldName === 'risk_rank' || fieldName === 'idling_rank') {
    if (!context.total_drivers) return null
    v = (v / context.total_drivers) * 100
    direction = 'lower_better'
  }
  const boundaries = resolveBandBoundaries(cfg)
  if (!boundaries.length) return null
  const count = Math.min(boundaries.length + 1, 5)
  const palette = BAND_PALETTES[count] || BAND_PALETTES[3]
  const bandFor = i => ({ band: i, count, key: palette[i].replace('text-', '') })
  for (let i = 0; i < boundaries.length; i++) {
    const b = boundaries[i]
    if (direction === 'higher_better') {
      if (v >= b) return bandFor(i)
    } else {
      // same first-boundary asymmetry as _bandClass(), see the note there
      if (i === 0 ? v < b : v <= b) return bandFor(i)
    }
  }
  return bandFor(boundaries.length)
}
